import {
  Injectable,
  NgZone
} from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subject
} from 'rxjs';

import { ApiService }               from '../api.service';
import { CableConnectOptionsModel } from './typings/websocket.cable-connect-options';
import { CableFrameModel }          from './typings/websocket.cable-frame';
import { CableSubscriptionModel }   from './typings/websocket.cable-subscription';

export type CableFrameType =
  | 'welcome'
  | 'ping'
  | 'confirm_subscription'
  | 'reject_subscription'
  | 'disconnect';

@Injectable({ providedIn: 'root' })
export class WebSocketService {
  private baseUrl       : string = this.getBaseUrl();
  private connectOpts  ?: CableConnectOptionsModel;
  private ws           ?: WebSocket;
  private pendingFrames : Array<{ command: string; identifier: string; data?: string }> = [];

  private connected$ = new BehaviorSubject<boolean>(false);
  private frames$    = new Subject<CableFrameModel>();
  private isOpen     = false;
  private messages$  = new Subject<{ identifier: string; message: any }>();

  constructor (
    private apiService: ApiService,
    private zone      : NgZone
  ) {}

  private buildCableUrl ( path : string, paramName : string, sessionId? : string) : string {
    const trimmedBase = this.baseUrl.replace(/\/+$/, '');
    const trimmedPath = path.startsWith('/') ? path : `/${path}`;
    const url         = `${trimmedBase}${trimmedPath}`;

    if (!sessionId) {
      return url;
    }

    const joiner = url.includes('?') ? '&' : '?';
    return `${url}${joiner}${encodeURIComponent(paramName)}=${encodeURIComponent(sessionId)}`;
  }

  connection$ () : Observable<boolean> {
    return this.connected$.asObservable();
  }

  connect ( opts : CableConnectOptionsModel = {} ) : void {
    this.disconnect();

    const cablePath        = opts.cablePath || '/cable';
    const sessionParamName = opts.sessionParamName || 'session_id';
    const sessionId        = opts.sessionId || this.readCookie('session_id');
    this.connectOpts       = { ...opts, cablePath, sessionParamName, sessionId };

    const url              = this.buildCableUrl(cablePath, sessionParamName, sessionId);

    this.ws                = new WebSocket(url);

    this.ws.onopen = () => {
      // ActionCable will still send "welcome" next.
      this.zone.run(() => {
        this.connected$.next(true);
      });
    };

    this.ws.onmessage = ( event : MessageEvent ) => {
      let frame: CableFrameModel;
      try {
        frame = JSON.parse(event.data);
      } catch {
        return;
      }

      // Ignore noisy pings
      if (frame.type === 'ping'){
        return;
      }

      this.zone.run(() => {
        this.frames$.next(frame);

        if (frame.type === 'welcome') {
          this.isOpen = true;
          this.flushPending();
          return;
        }

        // Real broadcast payload from stream
        if (frame.message && frame.identifier) {
          this.messages$.next({ identifier: frame.identifier, message: frame.message });
        }
      });
    };

    this.ws.onerror = (err) => {
    };

    this.ws.onclose = () => {
      this.isOpen = false;
      this.zone.run(() => this.connected$.next(false));
    };
  }

  disconnect () : void {
    this.pendingFrames = [];
    this.isOpen        = false;

    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      this.ws.close(1000, 'client disconnect');
    }
    this.ws = undefined;
    this.connected$.next(false);
  }

  /** (welcome, confirm_subscription, disconnect) */
  events$ () : Observable<CableFrameModel> {
    return this.frames$.asObservable();
  }

  private flushPending () : void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    const frames       = [...this.pendingFrames];
    this.pendingFrames = [];

    for (const f of frames) {
      this.ws.send(JSON.stringify(f));
    }
  }

  getBaseUrl () : string {
    const endpoint = this.apiService.getEndpoint();
    const baseUrl  = new URL(endpoint);

    baseUrl.protocol = 'wss:';

    return baseUrl.toString();
  }

  /** Only real broadcast payloads */
  notifications$ () : Observable<{ identifier : string; message : any }> {
    return this.messages$.asObservable();
  }

  private readCookie ( name : string ) : string | undefined {
    // simple cookie read
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : undefined;
  }

  subscribe ( channel : string, params : Record<string, any> = {}): CableSubscriptionModel {
    const identifier = JSON.stringify({ channel, ...params });

    // subscribe frame
    this.sendOrQueue({
      command: 'subscribe',
      identifier
    });

    return {
      identifier,
      unsubscribe: () => {
        this.sendOrQueue({ command: 'unsubscribe', identifier });
      },
      perform: ( action : string, payload : Record<string, any> = {}) => {
        const data = { action, ...payload };

        // ActionCable "perform"
        this.sendOrQueue({
          command: 'message',
          identifier,
          data   : JSON.stringify({ action, ...payload })
        });
      }
    };
  }

  private sendOrQueue ( frame : { command: string; identifier: string; data?: string } ) : void {

    if (!this.ws || this.ws.readyState !== WebSocket.OPEN || !this.isOpen) {
      this.pendingFrames.push(frame);
      return;
    }

    this.ws.send(JSON.stringify(frame));
  }

}
